import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreditCard, Search } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { CURRENCY_SYMBOL, formatCurrency } from "@/lib/currency";

const planLabels: Record<string, string> = {
  free: "مجاني", basic: "أساسي", premium: "متقدم", enterprise: "مؤسسي",
};

const statusLabels: Record<string, string> = {
  active: "نشط", expired: "منتهي", cancelled: "ملغي", suspended: "معلق",
};

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  expired: "bg-destructive/10 text-destructive",
  cancelled: "bg-muted text-muted-foreground",
  suspended: "bg-amber-100 text-amber-800",
};

const AccountantSubscriptions = () => {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlan, setFilterPlan] = useState("all");

  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ["accountant-all-subscriptions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nursery_subscriptions")
        .select("*, nurseries:nursery_id(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const filtered = subscriptions?.filter((s: any) => {
    if (search && !(s as any).nurseries?.name?.includes(search)) return false;
    if (filterStatus !== "all" && s.status !== filterStatus) return false;
    if (filterPlan !== "all" && s.plan !== filterPlan) return false;
    return true;
  }) || [];

  const totalMonthly = filtered.filter(s => s.status === "active").reduce((sum, s) => sum + (s.amount || 0), 0);

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-primary" />
          إدارة الاشتراكات
        </h1>
        <p className="text-muted-foreground text-sm mt-1">عرض جميع اشتراكات الحضانات</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">إجمالي الاشتراكات</p>
            <p className="text-2xl font-bold">{subscriptions?.length || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">نشطة</p>
            <p className="text-2xl font-bold text-green-600">
              {subscriptions?.filter(s => s.status === "active").length || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">تجريبية</p>
            <p className="text-2xl font-bold text-blue-600">
              {subscriptions?.filter(s => s.is_trial).length || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">الإيرادات الشهرية</p>
            <p className="text-2xl font-bold text-primary">{totalMonthly.toLocaleString("ar-EG")} <span className="text-sm">{CURRENCY_SYMBOL}</span></p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="بحث باسم الحضانة..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="active">نشط</SelectItem>
                <SelectItem value="expired">منتهي</SelectItem>
                <SelectItem value="cancelled">ملغي</SelectItem>
                <SelectItem value="suspended">معلق</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPlan} onValueChange={setFilterPlan}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الخطط</SelectItem>
                <SelectItem value="free">مجاني</SelectItem>
                <SelectItem value="basic">أساسي</SelectItem>
                <SelectItem value="premium">متقدم</SelectItem>
                <SelectItem value="enterprise">مؤسسي</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">الاشتراكات ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>الحضانة</TableHead>
                    <TableHead>الخطة</TableHead>
                    <TableHead>المبلغ</TableHead>
                    <TableHead>الدورة</TableHead>
                    <TableHead>تاريخ البدء</TableHead>
                    <TableHead>تاريخ الانتهاء</TableHead>
                    <TableHead>الحالة</TableHead>
                    <TableHead>خصم</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((sub: any) => (
                    <TableRow key={sub.id}>
                      <TableCell className="font-medium">{(sub as any).nurseries?.name || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{planLabels[sub.plan] || sub.plan}</Badge>
                        {sub.is_trial && <Badge className="mr-1 text-[10px]" variant="secondary">تجريبي</Badge>}
                      </TableCell>
                      <TableCell className="font-bold">{formatCurrency(sub.amount)}</TableCell>
                      <TableCell className="text-xs">{sub.billing_cycle === "yearly" ? "سنوي" : "شهري"}</TableCell>
                      <TableCell className="text-xs">{format(new Date(sub.started_at), "dd MMM yyyy", { locale: ar })}</TableCell>
                      <TableCell className="text-xs">
                        {sub.expires_at ? format(new Date(sub.expires_at), "dd MMM yyyy", { locale: ar }) : "—"}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[sub.status]}`}>
                          {statusLabels[sub.status] || sub.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {sub.discount_percent ? `${sub.discount_percent}%` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">لا توجد اشتراكات</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountantSubscriptions;
